import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";
import { useContent } from "../lib/content";
import { isVideo } from "../lib/site";

const BADGE_LABELS = { "360": "360°", video: "Video", boomerang: "Boomerang" };

const HEIGHTS = ["h-80", "h-56", "h-64", "h-56", "h-72", "h-64"];

const GalleryItem = ({ photo, index }) => (
  <Reveal delay={index * 0.06} className="break-inside-avoid mb-5">
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.4 }}
      className={`group relative overflow-hidden rounded-3xl glow-card ${photo.h} shadow-[0_8px_30px_rgba(145,0,141,0.08)]`}
      style={{ "--glow-delay": `${index * 0.7}s` }}
      data-testid={`gallery-item-${index}`}
    >
      {isVideo(photo.src) ? (
        <video
          src={photo.src}
          muted
          loop
          autoPlay
          playsInline
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
      ) : (
        <img
          src={photo.src}
          alt={photo.label}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
      )}
      {(photo.badge || photo.is360) && (
        <span className="absolute top-4 right-4 rounded-full bg-[#c9006f] text-white text-[10px] font-bold tracking-[0.18em] uppercase px-3 py-1.5">
          {BADGE_LABELS[photo.badge] || "360°"}
        </span>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#3c003a]/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <p className="absolute bottom-4 left-5 font-serif italic text-white text-lg opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
        {photo.label}
      </p>
    </motion.div>
  </Reveal>
);

export const Gallery = () => {
  const { gallery } = useContent();
  const photos = gallery.slice(0, 6).map((p, i) => ({ ...p, h: HEIGHTS[i % HEIGHTS.length] }));
  return (
  <section id="gallery" className="py-24 lg:py-32 bg-[#fafaf7]" data-testid="gallery-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Chapter number="04" label="The Gallery" />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <Reveal>
          <h2 className="font-serif text-[#121212] text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.1]">
            Moments worth <em className="text-[#91008d]">Capturing</em>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link
            to="/gallery"
            data-testid="view-full-gallery-btn"
            className="group inline-flex items-center gap-2 text-[#91008d] font-semibold border-b-2 border-[#c9006f] pb-1 hover:gap-4 transition-all duration-300"
          >
            View Full Gallery
            <ArrowRight size={18} className="text-[#a3005a]" />
          </Link>
        </Reveal>
      </div>
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-5">
        {photos.map((photo, i) => (
          <GalleryItem key={photo.id || i} photo={photo} index={i} />
        ))}
      </div>
    </div>
  </section>
  );
};
